import React, { Component } from "react";
import { Table, Grid, Icon } from "semantic-ui-react";
import find from "lodash/find";

import CardShell from "./CardShell";
import {
  subjectFinalGrade,
  conductFinalGrade,
  generalAverage,
  passFailLabel,
} from "../cardMath";
import {
  resolveSubjectTemplate,
  categoryMatches,
  categoryInList,
} from "../subjectTemplate";
import { clubRows, clubMark } from "../clubMembership";

const _ = { find };

const GROUP_HEADER = { backgroundColor: "#E0E0E0" };

const UNPLACED = Number.MAX_SAFE_INTEGER;

/**
 * Grade 12's report card from SY 2026-2027: one whole-year table on three
 * terms.
 *
 * It replaces the two semester tables. Grade 12 keeps the 2016 curriculum's
 * subjects, but the school now runs them across the whole year rather than
 * splitting them into a first and second semester, so there is nothing left for
 * two tables to divide. The registrar's own data said so before anyone did:
 * every Grade 12 subject assigned for SY 2026-2027 is marked semester 1,
 * including Filipino sa Piling Larangan, which is a second-semester subject
 * under the old split.
 *
 * That also fixed a card that could not have printed correctly. Three terms do
 * not divide evenly in two, so the second semester was a single term, and with
 * every subject marked semester 1 the second table had nothing to show at all.
 *
 * ONE RULE HERE IS UNLIKE EVERY OTHER CARD. Every other card renders the
 * curriculum template and leaves a row blank for a subject the section has not
 * been given, so a missing subject reads as INC. This one renders the subjects
 * the section actually carries. There is no whole-year Grade 12 template to
 * render: the 2016 constants are semester lists, and merging them would assert
 * a 16-subject curriculum nobody has confirmed - against the five subjects
 * these sections actually carry, that is eleven blank rows and an average stuck
 * on INC for the year. The template is still what sorts the rows and decides
 * which are core, which is all it can honestly say here.
 */
class CardSHS12 extends Component {
  state = { showAverage: false };

  componentDidMount() {
    this.setState({ showAverage: this.props.sectionStudent.showFinalGradeAverage });
  }

  componentDidUpdate(prevProps) {
    if (
      JSON.stringify(prevProps.sectionStudent) !==
      JSON.stringify(this.props.sectionStudent)
    ) {
      this.setState({
        showAverage: this.props.sectionStudent.showFinalGradeAverage,
      });
    }
  }

  toggleAverage = () =>
    this.setState((prev) => ({ showAverage: !prev.showAverage }));

  canSeeAverage() {
    const { userType } = this.props;
    return (
      userType === "admin" || userType === "principal" || userType === "registrar"
    );
  }

  gradeFor(sectionSubject) {
    if (!sectionSubject) return null;
    return _.find(
      this.props.summary.grades,
      (g) => g.sectionSubjectId === sectionSubject.sectionSubjectId
    );
  }

  /**
   * Splits the section's subjects into the form's two blocks.
   *
   * Core membership comes from the template's two core lists; anything else the
   * section carries is applied or specialized, including a subject the template
   * has never heard of - which still prints rather than vanishing. Rows keep
   * curriculum order, first semester's list before second's, because that is
   * the order a registrar reads them in.
   */
  groupSubjects(template) {
    const values = (block) => (block || []).map((slot) => slot.value);

    const coreOrder = values(template.Core1).concat(values(template.Core2));
    const appliedOrder = values(template.AppliedAndSpecializedSubjects1).concat(
      values(template.AppliedAndSpecializedSubjects2)
    );

    const assigned = (this.props.sectionSubjects || []).filter((o) => o.subject);

    const rank = (order, sectionSubject) => {
      const i = order.findIndex((v) =>
        categoryMatches(v, sectionSubject.subject.subjectCategory)
      );
      return i === -1 ? UNPLACED : i;
    };

    const sorted = (order, subjects) =>
      subjects.slice().sort((a, b) => {
        const diff = rank(order, a) - rank(order, b);
        if (diff !== 0) return diff;
        return String(a.subject.name).localeCompare(String(b.subject.name));
      });

    const isCore = (o) => categoryInList(coreOrder, o.subject.subjectCategory);

    return {
      core: sorted(coreOrder, assigned.filter(isCore)),
      applied: sorted(appliedOrder, assigned.filter((o) => !isCore(o))),
    };
  }

  render() {
    const { scheme, sectionStudent, teachers } = this.props;

    const section = sectionStudent.section;
    const periods = scheme.periods;

    const template = resolveSubjectTemplate(section, scheme);
    const grouped = this.groupSubjects(template);

    const finalGrades = [];
    const expectedCount = grouped.core.length + grouped.applied.length;

    const renderSubject = (sectionSubject, key) => {
      const grade = this.gradeFor(sectionSubject);
      const finalGrade = subjectFinalGrade(grade, periods);

      // subjectFinalGrade stays 0 until the last term is graded, so pushing it
      // would average a Term 1 card down to nothing. Leaving it out keeps
      // finalGrades short of expectedCount, which reads as INC.
      if (finalGrade) finalGrades.push(finalGrade);

      return (
        <Table.Row key={key} className="students-table-row">
          <Table.Cell>{sectionSubject.subject.name}</Table.Cell>
          {periods.map((p) => {
            const value = grade ? Number(grade[p.dbField]) || 0 : 0;
            return (
              <Table.Cell key={p.index} textAlign="center">
                {!value ? "" : value}
              </Table.Cell>
            );
          })}
          <Table.Cell textAlign="center">{!finalGrade ? "" : finalGrade}</Table.Cell>
          <Table.Cell textAlign="center">
            {passFailLabel(finalGrade, scheme.passingGrade)}
          </Table.Cell>
        </Table.Row>
      );
    };

    // Built before the JSX below reads finalGrades: rendering is what fills it,
    // and reading it first is what once printed INC on every Grade 12 card.
    const coreRows = grouped.core.map((s, i) => renderSubject(s, `core-${i}`));
    const appliedRows = grouped.applied.map((s, i) => renderSubject(s, `applied-${i}`));

    const classConduct =
      sectionStudent.classConducts && sectionStudent.classConducts.length !== 0
        ? sectionStudent.classConducts[0]
        : null;

    const conductFinal = conductFinalGrade(classConduct, periods);

    // The club is 5% of the general average here too.
    const clubs = clubRows(this.props.clubStudents, this.props.schoolYear);
    const clubFinal = subjectFinalGrade(
      clubs.length ? clubs[0].membership : null, periods);

    const average = generalAverage(finalGrades, conductFinal, expectedCount, clubFinal);
    const adviser = _.find(teachers, (t) => t.teacherId === section.teacherId);

    const columnCount = periods.length + 3;

    return (
      <CardShell {...this.props} adviser={adviser}>
        <Grid.Row>
          <Grid.Column width={16}>
            <Table celled size="small">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Subject
                  </Table.HeaderCell>
                  <Table.HeaderCell
                    colSpan={String(periods.length)}
                    textAlign="center"
                  >
                    Term
                  </Table.HeaderCell>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Final Grade
                  </Table.HeaderCell>
                  <Table.HeaderCell rowSpan="2" textAlign="center">
                    Remarks
                  </Table.HeaderCell>
                </Table.Row>
                <Table.Row>
                  {periods.map((p, i) => (
                    <Table.HeaderCell
                      key={p.index}
                      collapsing
                      textAlign="center"
                      style={
                        i === 0
                          ? { borderLeft: "1px solid rgba(34,36,38,.1)" }
                          : undefined
                      }
                    >
                      {p.shortLabel}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {this.renderGroupHeader("Core Subjects", columnCount)}
                {coreRows}

                {this.renderGroupHeader(
                  "Applied and Specialized Subjects",
                  columnCount
                )}
                {appliedRows}

                <Table.Row className="students-table-row">
                  <Table.Cell
                    colSpan={String(periods.length + 1)}
                    textAlign="center"
                  >
                    <strong>General Average</strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    <strong>
                      {/* The eye used to be the average's alternative, so
                          revealing it replaced the only control that could
                          hide it again - one way, until the page reloaded. It
                          stays beside the figure now. */}
                      {this.state.showAverage && average}
                      {this.canSeeAverage() && (
                        <Icon
                          name={this.state.showAverage ? "eye slash" : "eye"}
                          size="large"
                          title={
                            this.state.showAverage
                              ? "Hide the general average"
                              : "Show the general average"
                          }
                          style={{ cursor: "pointer", marginLeft: this.state.showAverage ? "0.5em" : 0 }}
                          onClick={this.toggleAverage}
                        />
                      )}
                    </strong>
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {this.state.showAverage && average !== "INC" ? "Promoted" : ""}
                  </Table.Cell>
                </Table.Row>

                <Table.Row className="students-table-row">
                  <Table.Cell>
                    <span>Conduct</span>
                  </Table.Cell>
                  {periods.map((p) => {
                    const v = classConduct ? classConduct[p.conductField] : "";
                    return (
                      <Table.Cell key={p.index} textAlign="center">
                        {!v ? "" : v}
                      </Table.Cell>
                    );
                  })}
                  <Table.Cell textAlign="center">{conductFinal}</Table.Cell>
                  <Table.Cell />
                </Table.Row>

                {/* Clubs print under Conduct and stay out of the average. */}
                {clubRows(this.props.clubStudents, this.props.schoolYear).map((club, i) => {
                  const clubFinal = subjectFinalGrade(club.membership, periods);
                  return (
                    <Table.Row key={`club-${i}`} className="students-table-row">
                      <Table.Cell>{club.label}</Table.Cell>
                      {periods.map((p) => (
                        <Table.Cell key={p.index} textAlign="center">
                          {clubMark(club.membership, scheme, p)}
                        </Table.Cell>
                      ))}
                      <Table.Cell textAlign="center">
                        {!clubFinal ? "" : clubFinal}
                      </Table.Cell>
                      <Table.Cell />
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid.Row>
      </CardShell>
    );
  }

  renderGroupHeader(label, columnCount) {
    return (
      <Table.Row className="students-table-row">
        <Table.Cell colSpan={String(columnCount)} style={GROUP_HEADER}>
          <strong>{label}</strong>
        </Table.Cell>
      </Table.Row>
    );
  }
}

export default CardSHS12;
